import {
  Alert,
  Button,
  Card,
  Form,
  FormProps,
  Icons,
  Input,
  Modal,
  ModalProps,
  Select,
  Space,
  Table,
  useSelect,
} from '@pankod/refine-antd'
import React, { useState } from 'react'

import ModalParticipantCandidateNPSN from '@resources/pendaftaran-diklat/pengajuan-peserta-didik/block/ModalParticipantCandidateNPSN'
import UploadSupportingDocument from '@resources/pendaftaran-diklat/pengajuan-peserta-didik/block/UploadSupportingDocument'
import { phone } from 'src/helpers/validator'
import useTableWithMeta, {
  TableQueryResultMeta,
} from 'src/hooks/useTableWithMeta'
import { Candidate } from '@resources/pendaftaran-diklat/types'
import { TCommonError } from 'src/interfaces/common'

type ModalFormAddCalonPesertaDidik = {
  modalProps: ModalProps
  formProps: FormProps
  onClose: () => void
}

const getRegisteredNuptk = ({
  candidates,
  registeredParticipants,
}: {
  candidates: {
    nama: string
    email: string
    nuptk: string
    fileUrl?: string
  }[]
  registeredParticipants: TableQueryResultMeta<Candidate, {}>
}): string | null => {
  const registeredParticipantNuptks = new Set(
    (
      registeredParticipants?.data?.data?.filter?.(
        ({ registered }) => registered,
      ) || []
    )?.map((data) => data?.nuptk) || [],
  )

  for (const candidate of candidates) {
    if (registeredParticipantNuptks.has(candidate?.nuptk)) {
      return candidate.nuptk
    }
  }

  return null
}

const ModalFormAddCalonPesertaDidik = ({
  formProps,
  modalProps,
  onClose,
}: ModalFormAddCalonPesertaDidik) => {
  const selectedProgramId = Form.useWatch('programId', formProps.form)
  const [showCandidateModal, setShowCandidateModal] = useState(false)
  const [isUploading, setIsUploading] = useState<Set<React.Key>>(new Set())
  const { tableProps, tableQueryResult } = useTableWithMeta<
    Candidate,
    TCommonError,
    unknown
  >({
    resource: 'diklat/calon-peserta-diklat',
    queryOptions: {
      enabled: showCandidateModal && !!selectedProgramId,
      select(data) {
        return {
          ...data,
          data: data?.data?.map?.((item) => ({
            ...item,
            key: `${item.nuptk}_${item.email}_${item.nama}`,
          })),
        }
      },
    },
    permanentFilter: [
      {
        field: 'pageSize',
        operator: 'eq',
        value: -1,
      },
      {
        field: 'programId',
        operator: 'eq',
        value: selectedProgramId,
      },
    ],
  })
  const { selectProps: selectProgramProps, queryResult: queryProgramResult } =
    useSelect({
      dataProviderName: 'lms',
      resource: `diklat/programs`,
      queryOptions: { enabled: modalProps.visible },
      optionLabel: 'name',
      optionValue: 'id',
      defaultValueQueryOptions: {
        enabled: false,
      },
      filters: [
        {
          field: 'pageSize',
          operator: 'eq',
          value: -1,
        },
      ],
    })
  return (
    <Modal
      {...modalProps}
      onCancel={onClose}
      title="Ajukan Calon Peserta Didik"
      okText="Ajukan Peserta"
      cancelText="Batal"
      maskClosable={false}
      afterClose={() => {
        formProps?.form?.resetFields()
      }}
      centered
      okButtonProps={{
        ...modalProps.okButtonProps,
        disabled: isUploading.size > 0,
      }}
    >
      {!selectedProgramId && (
        <Alert
          showIcon
          type="warning"
          message="Pilih program terlebih dahulu sebelum mengajukan calon peserta diklat."
          className="!mb-4"
        />
      )}
      <Form
        {...formProps}
        layout="vertical"
        validateMessages={{ required: '${label} harus diisi.' }}
      >
        <Form.Item
          label="Program"
          name="programId"
          required
          rules={[{ required: true }]}
          getValueFromEvent={(data) => {
            formProps?.form?.resetFields(['peserta'])
            return data
          }}
        >
          <Select
            {...selectProgramProps}
            options={[...(selectProgramProps?.options || [])]}
            loading={queryProgramResult.isFetching}
            onSearch={() => {}}
            placeholder="Pilih program"
            filterOption={(input, option) =>
              (option!.label as unknown as string)
                .toLowerCase?.()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.List
          name="peserta"
          rules={[
            {
              validator(
                _,
                value: {
                  nama: string
                  email: string
                  nuptk: string
                  fileUrl?: string
                }[],
              ) {
                if (!value?.length)
                  return Promise.reject(
                    'Daftar calon peserta yang diajukan tidak boleh kosong.',
                  )

                /** Validate nuptk is not duplicated */
                if (
                  new Set(value?.map?.((item) => item?.nuptk))?.size !==
                  value?.length
                )
                  return Promise.reject('NUPTK tidak boleh duplikat.')

                /** Validate nuptk already registered inside the program */
                const registeredNuptk = getRegisteredNuptk({
                  candidates: value,
                  registeredParticipants: tableQueryResult,
                })
                if (registeredNuptk) {
                  return Promise.reject(
                    `NUPTK "${registeredNuptk}" telah terdaftar pada program`,
                  )
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          {(fields, { add, remove }, { errors }) => {
            return (
              <>
                <Card
                  title={
                    <div className="before:content-['*'] before:text-[#a61d24] before:mr-1">
                      Daftar calon peserta didik yang diajukan
                    </div>
                  }
                  size="small"
                  extra={
                    <Space>
                      <Button
                        type="default"
                        onClick={() => setShowCandidateModal(true)}
                        icon={<Icons.UsergroupAddOutlined />}
                        disabled={!selectedProgramId}
                      >
                        Daftar Kandidat Peserta
                      </Button>
                    </Space>
                  }
                >
                  <div className="flex flex-col gap-2">
                    <Table
                      rowKey="key"
                      size="small"
                      pagination={false}
                      dataSource={fields}
                      footer={() => (
                        <Button
                          type="text"
                          onClick={() => add()}
                          icon={<Icons.PlusOutlined />}
                          size="small"
                          className="!m-0"
                          disabled={!selectedProgramId}
                        >
                          Tambah secara manual
                        </Button>
                      )}
                      columns={[
                        {
                          title: (
                            <div className="before:content-['*'] before:text-[#a61d24] before:mr-1">
                              Nama
                            </div>
                          ),
                          dataIndex: 'nama',
                          className: 'align-top',
                          render(_, { key, name, ...rest }) {
                            return (
                              <Form.Item
                                {...rest}
                                key={key}
                                className="!mb-0"
                                name={[name, 'nama']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Nama harus diisi.',
                                  },
                                ]}
                              >
                                <Input />
                              </Form.Item>
                            )
                          },
                        },
                        {
                          title: (
                            <div className="before:content-['*'] before:text-[#a61d24] before:mr-1">
                              Email
                            </div>
                          ),
                          dataIndex: 'email',
                          className: 'align-top',
                          render(_, { key, name, ...rest }) {
                            return (
                              <Form.Item
                                {...rest}
                                key={key}
                                className="!mb-0"
                                name={[name, 'email']}
                                rules={[
                                  {
                                    required: true,
                                    type: 'email',
                                    message: 'Email tidak valid.',
                                  },
                                ]}
                              >
                                <Input />
                              </Form.Item>
                            )
                          },
                        },
                        {
                          title: (
                            <div className="before:content-['*'] before:text-[#a61d24] before:mr-1">
                              NUPTK
                            </div>
                          ),
                          dataIndex: 'nuptk',
                          className: 'align-top',
                          render(_, { key, name, ...rest }) {
                            return (
                              <Form.Item
                                {...rest}
                                key={key}
                                className="!mb-0"
                                name={[name, 'nuptk']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'NUPTK harus diisi.',
                                  },
                                ]}
                              >
                                <Input />
                              </Form.Item>
                            )
                          },
                        },
                        {
                          title: <div>Nomor HP</div>,
                          dataIndex: 'phone',
                          className: 'align-top',
                          render(_, { key, name, ...rest }) {
                            return (
                              <Form.Item
                                {...rest}
                                key={key}
                                className="!mb-0"
                                name={[name, 'phone']}
                                rules={[
                                  {
                                    validator(_, value) {
                                      return phone(value)
                                    },
                                    message: 'Nomor HP tidak valid.',
                                  },
                                ]}
                              >
                                <Input />
                              </Form.Item>
                            )
                          },
                        },
                        {
                          title: 'Dokumen Pendukung',
                          dataIndex: 'fileUrl',
                          className: 'align-top',
                          render(_, { key, name, ...rest }) {
                            return (
                              <Form.Item
                                {...rest}
                                key={key}
                                className="!mb-0"
                                name={[name, 'fileUrl']}
                              >
                                <UploadSupportingDocument
                                  onUpload={(uploading) => {
                                    setIsUploading((prev) => {
                                      if (uploading) {
                                        return new Set(prev).add(name)
                                      }
                                      const next = new Set(prev)
                                      next.delete(name)
                                      return next
                                    })
                                  }}
                                />
                              </Form.Item>
                            )
                          },
                        },
                        {
                          title: 'Aksi',
                          dataIndex: 'action',
                          className: 'align-top',
                          render(_, { name }) {
                            return (
                              <Button
                                danger
                                onClick={() => remove(name)}
                                disabled={isUploading.has(name)}
                              >
                                Hapus
                              </Button>
                            )
                          },
                        },
                      ]}
                    />
                  </div>
                </Card>
                <div className="w-full mt-1">
                  <Form.ErrorList errors={errors} />
                </div>
                <ModalParticipantCandidateNPSN
                  onCancel={() => setShowCandidateModal(false)}
                  onOk={(candidates) => {
                    candidates.forEach(
                      ({ key: _, registered: __, ...candidate }) => {
                        add({
                          ...candidate,
                        })
                      },
                    )
                  }}
                  visible={showCandidateModal}
                  tableProps={tableProps}
                />
              </>
            )
          }}
        </Form.List>
      </Form>
    </Modal>
  )
}

export default ModalFormAddCalonPesertaDidik
